<script setup lang="ts">
import type { CampaignStatistics } from '@/types/types'

defineProps<{
  statistics: CampaignStatistics
  hasQrcode: boolean
  hasReward: boolean
  isEmailCampaign?: boolean
}>()

const { t } = useI18n()
const { formatNumber, formatPercentage, formatCurrency } = useNumber()
</script>

<template>
  <BaseTip v-if="!isEmailCampaign" class="mb-4" severity="info">
    <template #text>
      <div class="text-left">
        <p>{{ t('pages.campaign.stats.kpiInfoLine') }}</p>
      </div>
    </template>
  </BaseTip>

  <div :class="['grid grid-cols-1 gap-5 mb-6', isEmailCampaign ? 'md:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-4']">
    <AppStatsCard v-if="!isEmailCampaign">
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.messagesCount') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p class="font-semibold">{{ t('pages.campaign.stats.messagesTooltipTitle') }}</p>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-between gap-1 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ formatNumber(statistics.messagesCount) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
    <AppStatsCard v-if="!isEmailCampaign">
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.clicksRate') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p class="font-semibold">{{ t('pages.campaign.stats.clickTooltipTitle') }}</p>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipCountingRuleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.clickTooltipCountingRuleItem1') }}</li>
                  <li>{{ t('pages.campaign.stats.clickTooltipCountingRuleItem2') }}</li>
                </ul>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipExampleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.clickTooltipExampleItem1') }}</li>
                </ul>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-start gap-3 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ statistics.clicksCount ? formatNumber(statistics.clicksCount) : '-' }}
          </span>
          <span v-if="statistics.clicksPercentage > 0" class="text-muted-foreground">
            {{ formatPercentage(statistics.clicksPercentage, statistics.clicksPercentage < 1 ? 2 : 1) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
    <AppStatsCard>
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.visitsRate') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p class="font-semibold">{{ t('pages.campaign.stats.conversionTooltipTitle') }}</p>
                <p>{{ t('pages.campaign.stats.conversionTooltipReturnCountedTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.conversionTooltipReturnCountedItem1') }}</li>
                  <li>{{ t('pages.campaign.stats.conversionTooltipReturnCountedItem2') }}</li>
                </ul>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipCountingRuleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.conversionTooltipCountingRuleItem1') }}</li>
                  <li>{{ t('pages.campaign.stats.conversionTooltipCountingRuleItem2') }}</li>
                </ul>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipExampleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.conversionTooltipExampleItem1') }}</li>
                </ul>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-start gap-3 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ statistics.visitsCount ? formatNumber(statistics.visitsCount) : '-' }}
          </span>
          <span v-if="statistics.visitsPercentage > 0" class="text-muted-foreground">
            {{ formatPercentage(statistics.visitsPercentage, statistics.visitsPercentage < 1 ? 2 : 1) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
    <AppStatsCard v-if="hasQrcode && !isEmailCampaign">
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.qrcodeRate') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p class="font-semibold">{{ t('pages.campaign.stats.qrcodeTooltipTitle') }}</p>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipCountingRuleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.qrcodeTooltipCountingRuleItem1') }}</li>
                </ul>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipExampleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.qrcodeTooltipExampleItem1') }}</li>
                </ul>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-start gap-3 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ statistics.qrcodeCount ? formatNumber(statistics.qrcodeCount) : '-' }}
          </span>
          <span v-if="statistics.qrcodePercentage > 0" class="text-muted-foreground">
            {{ formatPercentage(statistics.qrcodePercentage, statistics.qrcodePercentage < 1 ? 2 : 1) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
    <AppStatsCard v-if="hasReward && !isEmailCampaign">
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.rewardRetrievedRate') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p>{{ t('pages.campaign.stats.rewardRetrievedTooltip') }}</p>
                <p>
                  {{ t('pages.campaign.stats.rewardRetrievedTooltip2') }}
                </p>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-start gap-3 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ statistics.rewardRetrievedCount ? formatNumber(statistics.rewardRetrievedCount) : '-' }}
          </span>
          <span v-if="statistics.rewardRetrievedPercentage > 0" class="text-muted-foreground">
            {{
              formatPercentage(statistics.rewardRetrievedPercentage, statistics.rewardRetrievedPercentage < 1 ? 2 : 1)
            }}
          </span>
        </div>
      </template>
    </AppStatsCard>
    <AppStatsCard>
      <template #title>
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-medium uppercase tracking-wider text-foreground-secondary">
            {{ t('pages.campaign.stats.generatedRevenue') }}
          </span>
          <AppTooltip width="w-[360px]" tooltip-position="bottom">
            <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
            <template #tooltip-content>
              <div class="flex flex-col gap-2 text-left">
                <p class="font-semibold">{{ t('pages.campaign.stats.revenueTooltipTitle') }}</p>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipCountingRuleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.revenueTooltipCountingRuleItem1') }}</li>
                  <li>{{ t('pages.campaign.stats.revenueTooltipCountingRuleItem2') }}</li>
                  <li>{{ t('pages.campaign.stats.revenueTooltipCountingRuleItem3') }}</li>
                </ul>
                <p class="font-semibold">{{ t('pages.campaign.stats.tooltipExampleTitle') }}</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>{{ t('pages.campaign.stats.revenueTooltipExampleItem1') }}</li>
                </ul>
              </div>
            </template>
          </AppTooltip>
        </div>
      </template>
      <template #content>
        <div class="flex flex-col flex-wrap items-baseline justify-between gap-1 md:flex-row">
          <span class="text-3xl font-extrabold text-primary">
            {{ formatCurrency(statistics.generatedRevenue) }}
          </span>
        </div>
      </template>
    </AppStatsCard>
  </div>
</template>
